import {fetchJSONMemoized} from '@gaia/utils/fetchJSON';
import differenceBy from 'lodash/differenceBy';
import keyBy from 'lodash/keyBy';
import memoize from 'lodash/memoize';

interface accessArgs {
    isAuthenticated: boolean;
    canAccessNonPublicSources: boolean;
}

export const fetchLayerTypes = memoize(
    async function fetchLayerTypes({
        isAuthenticated,
        canAccessNonPublicSources,
    }: accessArgs): Promise<{[key: string]: LayerType}> {
        return {
            ...(await fetchUserLayerTypes(isAuthenticated)),
            ...(await fetchGaiaLayerTypes(canAccessNonPublicSources)),
        };
    },
    (argsObj: accessArgs) => JSON.stringify(argsObj)
);

export const fetchCustomLayerTypes = memoize(
    async function fetchCustomLayerTypes({
        isAuthenticated,
        canAccessNonPublicSources,
    }: accessArgs): Promise<LayerType[]> {
        const userLayers = Object.values(await fetchUserLayerTypes(isAuthenticated));
        const gaiaLayers = Object.values(await fetchGaiaLayerTypes(canAccessNonPublicSources));

        return differenceBy(userLayers, gaiaLayers, 'id');
    },
    (argsObj: accessArgs) => JSON.stringify(argsObj)
);

async function fetchGaiaLayerTypes(
    canAccessNonPublicSources: boolean
): Promise<{[key: string]: LayerType}> {
    const url = `/mapsources/v2/gaiagps-ng-2019/?nonPublicSources=${canAccessNonPublicSources}`;
    const mapsources = await fetchJSONMemoized(url, {
        credentials: 'same-origin',
    });

    return keyBy(mapsources.mapSources, 'id');
}

async function fetchUserLayerTypes(isAuthenticated: boolean): Promise<{[key: string]: LayerType}> {
    if (!isAuthenticated) return {};

    const tilejsons = await fetchJSONMemoized('/api/objects/mapSource/tilejson/', {
        credentials: 'same-origin',
    });

    return keyBy(
        tilejsons.map((tilejson: any) => ({
            attribution: tilejson.attribution || '',
            basemap: true,
            center: null,
            default_opacity: 1,
            description: tilejson.description,
            styleURL: tilejson.mapboxGLStyle,
            icon: tilejson.icon,
            iconURL: tilejson.iconURL,
            maxzoom: tilejson.maxZoom,
            bounds: tilejson.bounds,
            minzoom: tilejson.minZoom,
            scheme: tilejson.scheme,
            tiles: tilejson.tiles,
            supportsCORS: tilejson.tiles[0].startsWith('https://'),
            subscriptionDataset: null,
            name: tilejson.name,
            id: tilejson.uniqueTilecacheKey,
            version: 1,
        })),
        'id'
    );
}
